import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock } from 'lucide-react';
import { getSkusByProductType, SKU_DIMENSIONS } from '../config/productSkuConfig';

const ChatGPTPlusRenewal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // 获取官网续费类型的所有产品SKU
  const renewalSkus = useMemo(() => {
    const skus = getSkusByProductType(SKU_DIMENSIONS.PRODUCT_TYPES.OFFICIAL_RENEWAL);
    // 按照时长排序：1个月、3个月、12个月
    const durationOrder = ['1month', '3months', '12months'];
    return skus.sort((a, b) => {
      return durationOrder.indexOf(a.duration) - durationOrder.indexOf(b.duration);
    });
  }, []);
  
  // 获取默认选中的套餐（12个月）
  const defaultPackage = renewalSkus.find(sku => sku.duration === '12months')?.id || 
                        (renewalSkus[0]?.id || '');
  
  const [formData, setFormData] = useState({
    selectedPackage: defaultPackage, // 默认选择12个月套餐
    accountToken: ''
  });
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [accountEmail, setAccountEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [verifiedToken, setVerifiedToken] = useState(''); // 保存验证成功的token
  
  
  // 获取套餐显示名称
  const getPackageDisplayName = (duration) => {
    const monthMap = {
      '1month': { zh: '1个月套餐', en: '1-Month' },
      '3months': { zh: '3个月套餐', en: '3-Month' },
      '12months': { zh: '12个月套餐', en: '12-Month' }
    };
    return monthMap[duration] || { zh: duration, en: duration };
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.accountToken.trim()) {
      alert(t('chatgptRenewal.form.tokenRequired', '请输入您的ChatGPT账户令牌'));
      return;
    }

    // 解析token - 支持JSON格式
    let tokenValue = formData.accountToken.trim();
    
    // 尝试解析JSON
    try {
      const jsonData = JSON.parse(tokenValue);
      // 只查找accessToken字段
      if (jsonData.accessToken) {
        tokenValue = jsonData.accessToken;
      }
    } catch (e) {
      // 不是JSON格式，保持原样
    }

    try {
      // 调用token验证接口
      const response = await fetch('/testapi/payment/renewal_controller/verify_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: tokenValue,
          package: formData.selectedPackage, // 同时发送选择的套餐SKU ID
          productId: renewalSkus.find(sku => sku.id === formData.selectedPackage)?.productId // 发送产品ID
        })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        // 验证成功，显示确认弹窗
        setAccountEmail(result.message); // message字段包含邮箱
        setVerifiedToken(tokenValue); // 保存验证成功的token
        setShowConfirmModal(true);
      } else {
        // 验证失败，显示错误弹窗
        setErrorMessage(result.message || t('chatgptRenewal.form.verifyError', 'Token验证失败，请检查您输入的令牌是否正确'));
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('API调用失败:', error);
      setErrorMessage(t('chatgptRenewal.form.networkError', '网络错误，请稍后重试'));
      setShowErrorModal(true);
    }
  };

  const handleConfirmUpgrade = () => {
    // 确认升级，执行支付跳转
    const selectedSku = renewalSkus.find(sku => sku.id === formData.selectedPackage);
    
    if (!selectedSku) {
      console.error('未找到选中的SKU');
      return;
    }
    
    // 构建跳转URL的查询参数
    const params = new URLSearchParams({
      email: accountEmail,
      productId: selectedSku.productId.toString(),
      skuId: selectedSku.id,
      quantity: '1',
      productType: selectedSku.productType,
      userConfig: selectedSku.userConfig,
      duration: selectedSku.duration,
      productName: selectedSku.product_name,
      price: selectedSku.price.toString(),
      originalPrice: selectedSku.originalPrice.toString(),
      currency: selectedSku.currency,
      platform: selectedSku.features.platform,
      users: selectedSku.features.users.toString(),
      warranty: selectedSku.features.warranty,
      accessToken: verifiedToken // 添加验证成功的accessToken
    });
    
    // 跳转到订单确认页面
    navigate(`/detail/info?${params.toString()}`);
    
    setShowConfirmModal(false);
  };

  const handleCancelUpgrade = () => {
    // 取消升级
    setShowConfirmModal(false);
    setAccountEmail('');
    setVerifiedToken(''); // 清空保存的token
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('renewal-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - 压缩版 */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-8 lg:py-12 px-4 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          {/* 主标题 */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-gray-900 mb-4 leading-tight">
            {t('chatgptRenewal.hero.title', 'Renew Your ChatGPT Plus')}
          </h1>
          
          {/* 副标题 */}
          <p className="text-lg font-light text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
            {t('chatgptRenewal.hero.subtitle', 'This self-service renewal tool lets you extend your existing ChatGPT Plus account at a better price. Secure, private renewals using official session tokens — no password required.')}
          </p>
          
          {/* 整合的CTA区域 - 增强立体感 */}
          <div className="relative bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 lg:p-10 max-w-3xl mx-auto shadow-2xl transform hover:scale-105 transition-all duration-300">
            {/* 装饰性背景元素 */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-100 to-transparent rounded-full opacity-50 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-gray-100 to-transparent rounded-full opacity-30 blur-2xl"></div>
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* 价格信息 - 增强立体感 */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 shadow-lg transform hover:scale-105 transition-transform duration-200">
                  <span className="mr-1">💰</span>
                  {t('chatgptRenewal.pricing.saveBadge', '节省 40%')}
                </div>
                <div className="flex items-baseline justify-center lg:justify-start gap-3 mb-3">
                  <span className="text-lg text-gray-500 line-through font-normal">$20.00</span>
                  <span className="text-3xl lg:text-4xl font-bold text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">$12.00</span>
                  <span className="text-sm text-gray-600 font-medium self-end">/month</span>
                </div>
                <p className="text-sm text-gray-700 font-medium leading-relaxed">
                  {t('chatgptRenewal.pricing.breakdown', '我们的续订服务每月为您节省 8 美元')}
                </p>
              </div>
              
              {/* CTA按钮 - 增强立体感 */}
              <div className="text-center">
                <button
                  onClick={scrollToForm}
                  className="w-full bg-gradient-to-r from-black to-gray-800 text-white px-8 py-4 rounded-xl text-lg font-bold hover:from-gray-800 hover:to-black transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl mb-4 transform active:scale-95"
                >
                  {t('chatgptRenewal.hero.cta', '开始使用')}
                </button>
                <p className="text-sm text-gray-600 font-medium leading-relaxed">
                  {t('chatgptRenewal.hero.ctaSubtitle', '即时设置，保留所有数据')}
                </p>
              </div>
            </div>
          </div>
          
          {/* 信任指标 */}
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-8 mt-6 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>{t('chatgptRenewal.trust.renewals', '8,000+ Successful Renewals')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{t('chatgptRenewal.trust.time', 'Complete in 5 Minutes')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>{t('chatgptRenewal.trust.security', 'Bank-Level Security')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area - 紧凑布局 */}
      <section className="py-6 lg:py-8 px-4 lg:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Left Content */}
            <div className="space-y-6">
              
              {/* 流程步骤 */}
              <div>
                <h2 className="text-2xl lg:text-3xl font-normal text-gray-900 mb-6 tracking-tight">
                  {t('chatgptRenewal.process.title', 'Complete Renewal in 3 Steps')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-xl p-6 text-center hover:bg-gray-100 transition-colors duration-200">
                    <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-medium shadow-lg">
                      1
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('chatgptRenewal.process.step1', 'Select Order')}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 text-center hover:bg-gray-100 transition-colors duration-200">
                    <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-medium shadow-lg">
                      2
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('chatgptRenewal.process.step2', 'Verify Info')}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-6 text-center hover:bg-gray-100 transition-colors duration-200">
                    <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-sm font-medium shadow-lg">
                      3
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {t('chatgptRenewal.process.step3', 'Complete Renewal')}
                    </div>
                  </div>
                </div>
                <p className="text-base text-gray-700 leading-relaxed">
                  {t('chatgptRenewal.process.description', 'We handle your renewal through a secure verification process. No password required, protecting your account security.')}
                </p>
              </div>

              {/* 价值主张 */}
              <div>
                <h2 className="text-xl lg:text-2xl font-normal text-gray-900 mb-5 tracking-tight">
                  {t('chatgptRenewal.value.title', 'Why Choose Our Service?')}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
                    <span className="text-2xl">💰</span>
                    <span className="text-sm font-medium text-gray-900">{t('chatgptRenewal.value.save', 'Save up to 40%')}</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
                    <span className="text-2xl">🧠</span>
                    <span className="text-sm font-medium text-gray-900">{t('chatgptRenewal.value.history', 'Keep all chat history')}</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
                    <span className="text-2xl">⚡</span>
                    <span className="text-sm font-medium text-gray-900">{t('chatgptRenewal.value.instant', 'Instant activation')}</span>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200">
                    <span className="text-2xl">🔒</span>
                    <span className="text-sm font-medium text-gray-900">{t('chatgptRenewal.value.secure', 'No password needed')}</span>
                  </div>
                </div>
              </div>

              {/* 安全保障 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-medium text-center mb-5 text-gray-900">
                  {t('chatgptRenewal.security.title', 'Security Guarantee')}
                </h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="text-sm text-gray-700">
                    <span className="block text-2xl mb-3">🔒</span>
                    <span className="font-medium">{t('chatgptRenewal.security.ssl', 'SSL Encryption')}</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="block text-2xl mb-3">🛡️</span>
                    <span className="font-medium">{t('chatgptRenewal.security.autoDelete', 'Token Auto-Delete')}</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="block text-2xl mb-3">📞</span>
                    <span className="font-medium">{t('chatgptRenewal.security.support', '24/7 Support')}</span>
                  </div>
                </div>
              </div>

              {/* FAQ部分 */}
              <div>
                <h2 className="text-xl lg:text-2xl font-normal text-gray-900 mb-5 tracking-tight">
                  {t('chatgptRenewal.faq.title', 'Frequently Asked Questions')}
                </h2>
                <div className="space-y-4">
                  {/* 如何获取session token */}
                  <div className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors duration-200">
                    <h3 className="text-base font-medium text-gray-900 mb-3">
                      {t('chatgptRenewal.faq.q1', 'How do I get my session token?')}
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-900 mb-2">
                          {t('chatgptRenewal.faq.quickMethod', '快速方法：')}
                        </p>
                        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                          <li>{t('chatgptRenewal.faq.quick1', '登录 ChatGPT 后访问：')}</li>
                          <li className="font-mono bg-white px-2 py-1 rounded border border-blue-200 inline-block ml-6">
                            <a href="https://chatgpt.com/api/auth/session" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              https://chatgpt.com/api/auth/session
                            </a>
                          </li>
                          <li>{t('chatgptRenewal.faq.quick2', '复制页面中 "accessToken" 的值')}</li>
                        </ol>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <p className="font-medium mb-2">{t('chatgptRenewal.faq.alternativeMethod', '备用方法：')}</p>
                        <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                          <li>{t('chatgptRenewal.faq.a1_1', 'Go to chat.openai.com and log in')}</li>
                          <li>{t('chatgptRenewal.faq.a1_2', 'Press F12 to open developer tools')}</li>
                          <li>{t('chatgptRenewal.faq.a1_3', 'Go to "Application" tab')}</li>
                          <li>{t('chatgptRenewal.faq.a1_4', 'Under Cookies, find __Secure-next-auth.session-token')}</li>
                          <li>{t('chatgptRenewal.faq.a1_5', 'Copy its value and paste it in the form')}</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  
                  {/* 安全性问题 */}
                  <div className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors duration-200">
                    <h3 className="text-base font-medium text-gray-900 mb-3">
                      {t('chatgptRenewal.faq.q2', 'Is this process safe?')}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('chatgptRenewal.faq.a2', 'Completely safe. We use SSL encryption for all communications. The token is only used to verify your identity and is immediately deleted after use. No sensitive information is stored.')}
                    </p>
                  </div>
                  
                  {/* 续订时间 */}
                  <div className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors duration-200">
                    <h3 className="text-base font-medium text-gray-900 mb-3">
                      {t('chatgptRenewal.faq.q3', 'How long does renewal take?')}
                    </h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {t('chatgptRenewal.faq.a3', 'Usually completed within 5 minutes. Once information is verified, your ChatGPT Plus subscription will be activated immediately.')}
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Right Form - 优化版 */}
            <div className="lg:col-span-1">
              <div 
                id="renewal-form"
                className="bg-white border border-gray-200 rounded-2xl p-8 shadow-xl sticky top-5"
              >
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-normal mb-3 text-gray-900">
                    {t('chatgptRenewal.form.title', 'Activate Your Subscription')}
                  </h2>
                  <p className="text-base text-gray-600 leading-relaxed">
                    {t('chatgptRenewal.form.subtitle', 'Use your purchased package to renew ChatGPT Plus')}
                  </p>
                </div>
                
                <div className="space-y-6">
                  {/* Step 1: Package Selection */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      {t('chatgptRenewal.form.step1', 'Step 1: Select Your Package')}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {renewalSkus.map((sku, index) => {
                        const displayName = getPackageDisplayName(sku.duration);
                        const isSelected = formData.selectedPackage === sku.id;
                        const isRecommended = sku.duration === '12months';
                        
                        return (
                          <div 
                            key={sku.id}
                            onClick={() => handleFormChange('selectedPackage', sku.id)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md text-center relative ${
                              isSelected 
                                ? 'border-black bg-black text-white' 
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            {/* 推荐标签 */}
                            {isRecommended && (
                              <div className="absolute -top-2 -right-2 text-2xl">
                                🔥
                              </div>
                            )}
                            <div className="font-medium text-sm mb-1">{displayName.zh}</div>
                            <div className="text-xs opacity-75 mb-2">{displayName.en}</div>
                            <div className="text-lg font-bold">${sku.price}</div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                      {t('chatgptRenewal.form.packageHint', 'Select the subscription package you purchased from our service')}
                    </p>
                  </div>
                  
                  {/* Step 2: Account Token */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-900 mb-3">
                      {t('chatgptRenewal.form.step2', 'Step 2: Link Your ChatGPT Account')}
                    </label>
                    <textarea
                      value={formData.accountToken}
                      onChange={(e) => handleFormChange('accountToken', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-black transition-all duration-200 shadow-sm hover:shadow-md min-h-[100px] resize-y"
                      placeholder={t('chatgptRenewal.form.tokenPlaceholder', 'Paste your token or JSON data here...')}
                    />
                    <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                      <a href="https://chatgpt.com/api/auth/session" target="_blank" rel="noopener noreferrer" className="text-black underline hover:text-gray-700">
                        {t('chatgptRenewal.form.howToGet', 'How to get this?')}
                      </a>{' '}
                      {t('chatgptRenewal.form.tokenHint', 'Supports plain token or JSON format')}
                    </p>
                  </div>
                  
                  {/* Submit Button */}
                  <button
                    onClick={handleSubmit}
                    className="w-full bg-black text-white py-4 rounded-lg text-base font-medium hover:bg-gray-800 transition-all duration-200 hover:-translate-y-1 hover:shadow-xl mt-8"
                  >
                    {t('chatgptRenewal.form.submit', '升级我的GPT账户到Plus')}
                  </button>
                  
                  <p className="text-center text-sm text-gray-600 leading-relaxed mt-4">
                    {t('chatgptRenewal.form.footer', 'Your subscription will be activated immediately')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 确认弹窗 */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✅</span>
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-2">
                {t('chatgptRenewal.confirm.title', '账户验证成功')}
              </h3>
              <p className="text-gray-600">
                {t('chatgptRenewal.confirm.subtitle', '请确认升级信息')}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="mb-3">
                <p className="text-sm text-gray-500 mb-1">
                  {t('chatgptRenewal.confirm.account', '账户邮箱')}
                </p>
                <p className="text-base font-medium text-gray-900">
                  {accountEmail}
                </p>
              </div>
              
              <div className="mb-3">
                <p className="text-sm text-gray-500 mb-1">
                  {t('chatgptRenewal.confirm.package', '选择的套餐')}
                </p>
                <p className="text-base font-medium text-gray-900">
                  {(() => {
                    const selectedSku = renewalSkus.find(sku => sku.id === formData.selectedPackage);
                    if (selectedSku) {
                      const displayName = getPackageDisplayName(selectedSku.duration);
                      return `${displayName.zh} - $${selectedSku.price}`;
                    }
                    return '';
                  })()}
                </p>
              </div>
              
              <div className="pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  {t('chatgptRenewal.confirm.question', '确认要将此账户升级到ChatGPT Plus吗？')}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleCancelUpgrade}
                className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                {t('chatgptRenewal.confirm.cancel', '取消')}
              </button>
              <button
                onClick={handleConfirmUpgrade}
                className="flex-1 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                {t('chatgptRenewal.confirm.confirm', '确认升级')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 错误弹窗 */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">❌</span>
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-2">
                {t('chatgptRenewal.error.title', '账户验证失败')}
              </h3>
              <p className="text-gray-600">
                {t('chatgptRenewal.error.subtitle', '请检查您的输入信息')}
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 leading-relaxed">
                {errorMessage}
              </p>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-2">{t('chatgptRenewal.error.possibleReasons', '可能的原因：')}</p>
                <ul className="list-disc list-inside space-y-1 text-gray-500">
                  <li>{t('chatgptRenewal.error.reason1', 'Token已过期或无效')}</li>
                  <li>{t('chatgptRenewal.error.reason2', '账户未登录或会话已断开')}</li>
                  <li>{t('chatgptRenewal.error.reason3', '复制的Token不完整')}</li>
                  <li>{t('chatgptRenewal.error.reason4', '网络连接问题')}</li>
                </ul>
              </div>
              
              <div className="text-sm text-gray-600">
                <p className="font-medium mb-2">{t('chatgptRenewal.error.solutions', '解决方案：')}</p>
                <ol className="list-decimal list-inside space-y-1 text-gray-500">
                  <li>{t('chatgptRenewal.error.solution1', '重新登录ChatGPT账户')}</li>
                  <li>{t('chatgptRenewal.error.solution2', '访问 https://chatgpt.com/api/auth/session 获取最新Token')}</li>
                  <li>{t('chatgptRenewal.error.solution3', '确保复制完整的accessToken值')}</li>
                </ol>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                {t('chatgptRenewal.error.close', '关闭')}
              </button>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage('');
                  // 聚焦到token输入框
                  document.querySelector('textarea[placeholder*="token"]')?.focus();
                }}
                className="flex-1 px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                {t('chatgptRenewal.error.retry', '重新输入')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatGPTPlusRenewal;